// Paints one page of a quiz document onto an offscreen canvas.
// Output contract is PdfCanvasViewer's: a bitmap plus its aspect, drawn as the board
// background underneath every stroke, so ink and the pager behave exactly as on a PDF.

import {
  optionLabel,
  quizBodyFootLabel,
  quizBodyPageIndexes,
  quizQuestionNumber,
  QUIZ_ANSWER_MARK,
  QUIZ_IMAGE_CLOSE,
  QUIZ_IMAGE_OPEN,
  QUIZ_QUESTION_HINT,
  type QuizBody,
  type QuizQuestion,
} from './body';
import {
  quizBlockFit,
  quizSoloFit,
  quizStemLines,
  QUIZ_STEM_GAP,
  QUIZ_STEM_LINE,
  QUIZ_CARD_INSET,
  QUIZ_FOOT_HEIGHT,
  QUIZ_HEADING_ADVANCE,
  QUIZ_OPTION_GAP,
  QUIZ_OPTION_LINE,
  QUIZ_PROMPT_GAP,
  QUIZ_PROMPT_LINE,
  QUIZ_QUESTION_GAP,
  QUIZ_QUESTION_TOP,
  QUIZ_TEXT_INSET,
  type QuizBlockFit,
} from './quizLayout';
import { quizImage } from './images';

/** Board-space width of the artboard - the renderer scales the bitmap to exactly this. */
const ARTBOARD_WIDTH = 1000;
/** Text stays readable at fit zoom without the memory cost of a full-DPR page. */
const MAX_DPR = 2;

const QUIZ_ASPECT = 4 / 3;

const SANS = "'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const DISPLAY = "'Be Vietnam Pro', 'Inter', 'Segoe UI', Roboto, Arial, sans-serif";

export interface QuizSurface {
  canvas: HTMLCanvasElement;
  aspect: number;
}

interface Palette {
  text: string;
  textBody: string;
  textMuted: string;
  border: string;
  success: string;
  danger: string;
  canvas: string;
}

let palette: Palette | null = null;

/** Tokens stay the single source of truth - canvas cannot read `var()` on its own. */
function readVar(name: string, fallback: string): string {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const value = window.getComputedStyle(window.document.documentElement).getPropertyValue(name);
  const trimmed = value ? value.replace(/^\s+/, '').replace(/\s+$/, '') : '';
  return trimmed || fallback;
}

function colors(): Palette {
  if (palette) return palette;
  palette = {
    text: readVar('--color-text', '#111827'),
    textBody: readVar('--color-text-body', '#364153'),
    textMuted: readVar('--color-text-muted', '#6B7280'),
    border: readVar('--color-border', '#E5E7EB'),
    success: readVar('--color-success', '#16A34A'),
    danger: readVar('--color-danger', '#DC2626'),
    canvas: readVar('--color-canvas', '#F2F2F2'),
  };
  return palette;
}

function font(weight: number, size: number, stack: string): string {
  return weight + ' ' + size + 'px ' + stack;
}

interface Sheet {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
}

function createSheet(aspect: number): Sheet {
  const ratio = typeof window !== 'undefined' && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
  const dpr = Math.min(Math.max(ratio, 1), MAX_DPR);
  const width = ARTBOARD_WIDTH;
  const height = Math.round(ARTBOARD_WIDTH / aspect);
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Không thể khởi tạo vùng hiển thị tài liệu.');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  return { canvas, ctx, width, height };
}

/** arcTo keeps this valid on Chrome 58, which has no ctx.roundRect. */
function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const MARKS = new RegExp(QUIZ_ANSWER_MARK, 'g');

/** Image URLs of the body being painted; set for the length of one synchronous paintQuizPage. */
let pageImages: string[] = [];

const TOKEN = new RegExp(QUIZ_IMAGE_OPEN + '(\\d+)' + QUIZ_IMAGE_CLOSE, 'g');
const BLOCK_TOKEN = new RegExp('^' + QUIZ_IMAGE_OPEN + '(\\d+)' + QUIZ_IMAGE_CLOSE + '$');
/** Half a token, left where a clamp cut one. */
const STRAY = new RegExp('[' + QUIZ_IMAGE_OPEN + QUIZ_IMAGE_CLOSE + ']', 'g');
/** A line slot held by the figure on the line above it. */
const FIGURE_SPACER = '\u0004';
/** Taller than this (CSS px at 16px) is a figure on its own lines, not an inline formula. */
const INLINE_MAX_NATURAL = 48;
const FIGURE_MAX_HEIGHT = 220;
const MISSING_IMAGE = '[hình]';

function fontPx(ctx: CanvasRenderingContext2D): number {
  const match = /(\d+(?:\.\d+)?)px/.exec(ctx.font);
  return match ? Number(match[1]) : 16;
}

interface ImageBox {
  image: HTMLImageElement | null;
  width: number;
  height: number;
  figure: boolean;
}

/** Painted size of image `index`: formulas scale with the text, figures keep their pixels. */
function imageBox(ctx: CanvasRenderingContext2D, index: number): ImageBox {
  const url = pageImages[index];
  const image = url ? quizImage(url) : null;
  if (!image) return { image: null, width: ctx.measureText(MISSING_IMAGE).width, height: 0, figure: false };
  const natural = { width: image.naturalWidth, height: image.naturalHeight };
  if (natural.height > INLINE_MAX_NATURAL) {
    const height = Math.min(natural.height, FIGURE_MAX_HEIGHT);
    return { image, width: natural.width * height / natural.height, height, figure: true };
  }
  // MathJax SVGs size in ex against a 16px default; capped so a fraction only grazes its neighbours
  const size = fontPx(ctx);
  const height = Math.min(natural.height * size / 16, size * 1.75);
  return { image, width: natural.width * height / natural.height, height, figure: false };
}

type Segment = { text: string } | { image: number };

function segments(word: string): Segment[] {
  if (word.indexOf(QUIZ_IMAGE_OPEN) === -1) return [{ text: word.replace(STRAY, '') }];
  const out: Segment[] = [];
  let last = 0;
  word.replace(TOKEN, (match: string, index: string, at: number) => {
    if (at > last) out.push({ text: word.slice(last, at).replace(STRAY, '') });
    out.push({ image: Number(index) });
    last = at + match.length;
    return match;
  });
  if (last < word.length) out.push({ text: word.slice(last).replace(STRAY, '') });
  return out;
}

function hasTokens(text: string): boolean {
  return text.indexOf(QUIZ_IMAGE_OPEN) >= 0 || text.indexOf(QUIZ_IMAGE_CLOSE) >= 0;
}

function wordWidth(ctx: CanvasRenderingContext2D, word: string): number {
  const plain = word.indexOf(QUIZ_ANSWER_MARK) >= 0 ? word.replace(MARKS, '') : word;
  return segments(plain).reduce((sum, part) => sum + ('image' in part ? imageBox(ctx, part.image).width : ctx.measureText(part.text).width), 0);
}

/** Width of text as painted: answer marks take no room, image tokens take their image's. */
function measure(ctx: CanvasRenderingContext2D, text: string): number {
  if (!hasTokens(text)) return ctx.measureText(text.indexOf(QUIZ_ANSWER_MARK) >= 0 ? text.replace(MARKS, '') : text).width;
  const words = text.split(' ');
  const space = ctx.measureText(' ').width;
  return words.reduce((sum, word) => sum + wordWidth(ctx, word), 0) + space * Math.max(0, words.length - 1);
}

/** The image index when `word` is a lone figure token, else -1. */
function figureIndex(ctx: CanvasRenderingContext2D, word: string): number {
  const match = BLOCK_TOKEN.exec(word.replace(MARKS, ''));
  if (!match) return -1;
  const index = Number(match[1]);
  return imageBox(ctx, index).figure ? index : -1;
}

interface Wrapped {
  lines: string[];
  /** Something did not fit in `maxLines`. */
  truncated: boolean;
}

/** Word wrap; a figure takes the lines its height needs (at least two), shrunk to what is left. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number, lineHeight: number): Wrapped {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const figure = hasTokens(words[i]) ? figureIndex(ctx, words[i]) : -1;
    if (figure >= 0) {
      if (line) {
        lines.push(line);
        line = '';
      }
      const room = maxLines - lines.length;
      if (room < 2) return { lines, truncated: true };
      const wanted = Math.max(2, Math.ceil((imageBox(ctx, figure).height + 8) / lineHeight));
      lines.push(words[i]);
      for (let j = 1; j < Math.min(wanted, room); j++) lines.push(FIGURE_SPACER);
      if (lines.length === maxLines) return { lines, truncated: i < words.length - 1 };
      continue;
    }
    const next = line ? line + ' ' + words[i] : words[i];
    if (line && measure(ctx, next) > maxWidth) {
      lines.push(line);
      line = words[i];
      if (lines.length === maxLines) return { lines, truncated: true };
    } else {
      line = next;
    }
  }
  if (line) {
    if (lines.length < maxLines) lines.push(line);
    else return { lines, truncated: true };
  }
  return { lines, truncated: false };
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number, lineHeight: number): string[] {
  return wrap(ctx, text, maxWidth, maxLines, lineHeight).lines;
}

/** Draws image `index` with its left edge at `x`, centred on the text line at `y`; returns its width. */
function drawInlineImage(ctx: CanvasRenderingContext2D, index: number, x: number, y: number): number {
  const box = imageBox(ctx, index);
  if (!box.image) {
    ctx.fillText(MISSING_IMAGE, x, y);
    return box.width;
  }
  const middle = y + fontPx(ctx) * 0.55;
  ctx.drawImage(box.image, x, middle - box.height / 2, box.width, box.height);
  return box.width;
}

/** A figure over `slots` lines from `y`, scaled down to fit both the slots and the width. */
function drawFigure(ctx: CanvasRenderingContext2D, index: number, x: number, y: number, maxWidth: number, slots: number, lineHeight: number): void {
  const box = imageBox(ctx, index);
  if (!box.image) {
    ctx.fillText(MISSING_IMAGE, x, y);
    return;
  }
  const scale = Math.min(1, (slots * lineHeight - 8) / box.height, maxWidth / box.width);
  ctx.drawImage(box.image, x, y + 2, box.width * scale, box.height * scale);
}

/** One line; words carrying the answer mark paint in `answerColor`, the rest in the current fill. */
function fillLine(ctx: CanvasRenderingContext2D, line: string, x: number, y: number, answerColor: string): void {
  if (line === FIGURE_SPACER) return;
  if (line.indexOf(QUIZ_ANSWER_MARK) === -1 && !hasTokens(line)) {
    ctx.fillText(line, x, y);
    return;
  }
  const base = ctx.fillStyle;
  const words = line.split(' ');
  let cursor = x;
  for (let i = 0; i < words.length; i++) {
    const answer = words[i].indexOf(QUIZ_ANSWER_MARK) >= 0;
    const word = answer ? words[i].replace(MARKS, '') : words[i];
    ctx.fillStyle = answer ? answerColor : base;
    if (!hasTokens(word)) {
      ctx.fillText(word, cursor, y);
      cursor += ctx.measureText(word + ' ').width;
      continue;
    }
    segments(word).forEach((part) => {
      if ('image' in part) {
        cursor += drawInlineImage(ctx, part.image, cursor, y);
        return;
      }
      ctx.fillText(part.text, cursor, y);
      cursor += ctx.measureText(part.text).width;
    });
    cursor += ctx.measureText(' ').width;
  }
  ctx.fillStyle = base;
}

/** Drops the last word, or the last character of plain text, so an ellipsis fits. */
function shorten(line: string): string {
  if (!hasTokens(line)) return line.slice(0, -1);
  const cut = line.lastIndexOf(' ');
  return cut > 0 ? line.slice(0, cut) : '';
}

/** Draws wrapped text from `y`, ellipsised when it did not fit, and returns the y past the last line. */
function clippedParagraph(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
  answerColor = '#16A34A',
): number {
  const wrapped = wrap(ctx, text, maxWidth, maxLines, lineHeight);
  const lines = wrapped.lines;
  if (lines.length === 0) return y;
  const lastIndex = lines.length - 1;
  if (wrapped.truncated && lines[lastIndex] !== FIGURE_SPACER && figureIndex(ctx, lines[lastIndex]) === -1) {
    let last = lines[lastIndex];
    while (last.length > 1 && measure(ctx, last + '…') > maxWidth) last = shorten(last);
    lines[lastIndex] = last + '…';
  }
  for (let i = 0; i < lines.length; i++) {
    const figure = hasTokens(lines[i]) ? figureIndex(ctx, lines[i]) : -1;
    if (figure >= 0) {
      let slots = 1;
      while (i + slots < lines.length && lines[i + slots] === FIGURE_SPACER) slots += 1;
      drawFigure(ctx, figure, x, y + i * lineHeight, maxWidth, slots, lineHeight);
      continue;
    }
    fillLine(ctx, lines[i], x, y + i * lineHeight, answerColor);
  }
  return y + lines.length * lineHeight;
}

function hairline(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

/** `A.` is the painted label; the answer line wants the bare letter. */
function optionLetter(index: number): string {
  return optionLabel(index).replace(/\.$/, '');
}

/** Every option the reveal marks. A tự luận question has none, a nhiều-đáp-án question several. */
function answerKeys(question: QuizQuestion): number[] {
  const keys = question.correctIndexes && question.correctIndexes.length > 0
    ? question.correctIndexes
    : (question.correct >= 0 ? [question.correct] : []);
  return keys.filter((index) => index >= 0 && index < question.options.length);
}

/** Heading line `Câu N: hint`, with the answer letters on the right once revealed. */
function paintHeading(ctx: CanvasRenderingContext2D, c: Palette, question: QuizQuestion, number: string, revealed: boolean, left: number, textWidth: number, y: number): void {
  const keys = answerKeys(question);
  ctx.font = font(700, 18, SANS);
  ctx.fillStyle = c.text;
  ctx.fillText('Câu ' + number + ': ' + (question.hint || QUIZ_QUESTION_HINT), left, y);
  if (revealed && keys.length > 0) {
    // the answer letter rides the heading line, so revealing costs a stacked page no height at all
    ctx.font = font(600, 15, SANS);
    ctx.fillStyle = c.success;
    ctx.textAlign = 'right';
    ctx.fillText('Đáp án đúng: ' + keys.map(optionLetter).join(', '), left + textWidth, y + 3);
    ctx.textAlign = 'left';
  }
}

function promptText(question: QuizQuestion, revealed: boolean): string {
  return revealed && question.revealPrompt ? question.revealPrompt : question.prompt;
}

/** One `Câu N` block; returns the y the next block starts at. `number` is absolute, not per page. */
function paintQuizQuestion(
  ctx: CanvasRenderingContext2D,
  c: Palette,
  question: QuizQuestion,
  number: string,
  revealed: boolean,
  left: number,
  textWidth: number,
  top: number,
  fit: QuizBlockFit,
): number {
  let y = top;
  const keys = answerKeys(question);
  paintHeading(ctx, c, question, number, revealed, left, textWidth, y);
  y += QUIZ_HEADING_ADVANCE;

  ctx.font = font(400, 17, SANS);
  ctx.fillStyle = c.textBody;
  y = clippedParagraph(ctx, promptText(question, revealed), left, y, textWidth, QUIZ_PROMPT_LINE, fit.promptLines, c.success) + QUIZ_PROMPT_GAP;
  y = paintOptions(ctx, c, question, keys, revealed, left, textWidth, y, fit);
  return y + QUIZ_QUESTION_GAP;
}

/** Width kept at the right of a statement for its Đúng / Sai mark. */
const STATEMENT_MARK_WIDTH = 96;

/** Option rows from `y`; a Đúng - Sai statement gets `a)` and its Đúng / Sai column. Returns the y past them. */
function paintOptions(
  ctx: CanvasRenderingContext2D,
  c: Palette,
  question: QuizQuestion,
  keys: number[],
  revealed: boolean,
  left: number,
  textWidth: number,
  top: number,
  fit: QuizBlockFit,
): number {
  let y = top;
  const statements = question.statementKeys;
  const shown = Math.min(fit.optionsShown, question.options.length);
  for (let i = 0; i < shown; i++) {
    if (statements) {
      y = paintStatement(ctx, c, question.options[i], i, statements[i], revealed, left, textWidth, y, fit.optionLines);
      continue;
    }
    const marked = revealed && keys.indexOf(i) >= 0;
    const label = optionLabel(i);
    ctx.font = font(600, 16, SANS);
    if (marked) {
      // green chip behind the letter - the review vocabulary IZTeach already uses for a right answer
      const chipWidth = ctx.measureText(label).width + 20;
      roundRectPath(ctx, left + 10, y - 4, chipWidth, 26, 13);
      ctx.fillStyle = c.success;
      ctx.fill();
      ctx.fillStyle = '#ffffff';
    } else {
      ctx.fillStyle = c.textMuted;
    }
    ctx.fillText(label, left + 20, y);
    // weight stays 400 either way, so revealing never re-wraps an option onto another line
    ctx.font = font(400, 16, SANS);
    ctx.fillStyle = marked ? c.success : c.textBody;
    y = clippedParagraph(ctx, question.options[i], left + 56, y, textWidth - 56, QUIZ_OPTION_LINE, fit.optionLines) + QUIZ_OPTION_GAP;
  }

  // never silently swallow options - a dropped answer key would make the reveal look broken
  if (shown < question.options.length) {
    ctx.font = font(400, 15, SANS);
    ctx.fillStyle = c.textMuted;
    ctx.fillText('Còn ' + (question.options.length - shown) + (statements ? ' ý' : ' đáp án') + ', xem trong kho bài tập.', left + 20, y);
    y += QUIZ_OPTION_LINE + QUIZ_OPTION_GAP;
  }
  return y;
}

/** `a) statement ... Đúng / Sai`; the reveal turns the column into the key. */
function paintStatement(
  ctx: CanvasRenderingContext2D,
  c: Palette,
  text: string,
  index: number,
  key: boolean | null | undefined,
  revealed: boolean,
  left: number,
  textWidth: number,
  y: number,
  lines: number,
): number {
  ctx.font = font(600, 16, SANS);
  ctx.fillStyle = c.textMuted;
  ctx.fillText(String.fromCharCode(97 + (index % 26)) + ')', left + 20, y);
  ctx.textAlign = 'right';
  if (revealed && typeof key === 'boolean') {
    ctx.font = font(700, 16, SANS);
    ctx.fillStyle = key ? c.success : c.danger;
    ctx.fillText(key ? 'Đúng' : 'Sai', left + textWidth, y);
  } else {
    ctx.font = font(400, 16, SANS);
    ctx.fillStyle = c.textMuted;
    ctx.fillText('Đ / S', left + textWidth, y);
  }
  ctx.textAlign = 'left';
  ctx.font = font(400, 16, SANS);
  ctx.fillStyle = c.textBody;
  return clippedParagraph(ctx, text, left + 56, y, textWidth - 56 - STATEMENT_MARK_WIDTH, QUIZ_OPTION_LINE, lines) + QUIZ_OPTION_GAP;
}

/** Lines `text` wants at `width`, capped so a runaway passage does not loop long. */
function wantedLines(ctx: CanvasRenderingContext2D, text: string, width: number, lineHeight: number): number {
  return Math.max(1, wrapLines(ctx, text, width, 40, lineHeight).length);
}

/** A question that owns the page: stem (câu chùm), prompt, then options or statements, from `top` to `bottom`. */
function paintSoloQuestion(
  ctx: CanvasRenderingContext2D,
  c: Palette,
  question: QuizQuestion,
  number: string,
  revealed: boolean,
  left: number,
  textWidth: number,
  top: number,
  bottom: number,
): void {
  const keys = answerKeys(question);
  const prompt = promptText(question, revealed);
  ctx.font = font(400, 17, SANS);
  const wantedPrompt = wantedLines(ctx, prompt, textWidth, QUIZ_PROMPT_LINE);
  ctx.font = font(400, 16, SANS);
  const optionWidth = textWidth - 56 - (question.statementKeys ? STATEMENT_MARK_WIDTH : 0);
  let wantedOption = 1;
  for (let i = 0; i < question.options.length; i++) wantedOption = Math.max(wantedOption, wantedLines(ctx, question.options[i], optionWidth, QUIZ_OPTION_LINE));

  paintHeading(ctx, c, question, number, revealed, left, textWidth, top);
  let y = top + QUIZ_HEADING_ADVANCE;
  let used = 0;
  if (question.stem) {
    ctx.font = font(400, 16, SANS);
    const stemLines = quizStemLines(bottom - top, wantedLines(ctx, question.stem, textWidth - 16, QUIZ_STEM_LINE), wantedPrompt, question.options.length);
    if (stemLines > 0) {
      // the shared passage sits on a tinted band so the child's own prompt reads as the question
      const stemTop = y;
      ctx.fillStyle = c.textBody;
      const stemEnd = clippedParagraph(ctx, question.stem, left + 16, y, textWidth - 16, QUIZ_STEM_LINE, stemLines);
      ctx.fillStyle = c.border;
      ctx.fillRect(left, stemTop - 2, 3, stemEnd - stemTop + 2);
      used = stemEnd - y + QUIZ_STEM_GAP;
      y = stemEnd + QUIZ_STEM_GAP;
    }
  }

  const fit = quizSoloFit(bottom - top - used, question.options.length, wantedPrompt, wantedOption);
  ctx.font = font(400, 17, SANS);
  ctx.fillStyle = c.textBody;
  y = clippedParagraph(ctx, prompt, left, y, textWidth, QUIZ_PROMPT_LINE, fit.promptLines, c.success) + QUIZ_PROMPT_GAP;
  paintOptions(ctx, c, question, keys, revealed, left, textWidth, y, fit);
}

/** Paints a one-based page of a quiz; `revealed` marks the answer keys. */
export function paintQuizPage(body: QuizBody, page: number, revealed: boolean): QuizSurface {
  pageImages = body.images || [];
  try {
    return paintPage(body, page, revealed);
  } finally {
    pageImages = [];
  }
}

function paintPage(body: QuizBody, page: number, revealed: boolean): QuizSurface {
  const sheet = createSheet(QUIZ_ASPECT);
  const ctx = sheet.ctx;
  const c = colors();
  const questions = body.questions;
  // several questions per page, numbered absolutely - page 2 opens on Câu 4, not Câu 1
  const indexes = quizBodyPageIndexes(questions, page);

  ctx.fillStyle = c.canvas;
  ctx.fillRect(0, 0, sheet.width, sheet.height);

  // the document card itself - radius 22, the Figma value, carried by --radius-doc
  const inset = QUIZ_CARD_INSET;
  const cardWidth = sheet.width - inset * 2;
  const cardHeight = sheet.height - inset * 2;
  ctx.save();
  ctx.shadowColor = 'rgba(16, 24, 40, 0.10)';
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 8;
  roundRectPath(ctx, inset, inset, cardWidth, cardHeight, 22);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.restore();
  roundRectPath(ctx, inset + 0.5, inset + 0.5, cardWidth - 1, cardHeight - 1, 22);
  ctx.strokeStyle = c.border;
  ctx.lineWidth = 1;
  ctx.stroke();

  const left = inset + QUIZ_TEXT_INSET;
  const right = sheet.width - inset - QUIZ_TEXT_INSET;
  const textWidth = right - left;

  ctx.font = font(700, 30, DISPLAY);
  ctx.fillStyle = c.text;
  // a long title clips instead of bleeding out
  clippedParagraph(ctx, body.title, left, inset + 22, textWidth, 36, 1);
  ctx.font = font(400, 17, SANS);
  ctx.fillStyle = c.textMuted;
  ctx.fillText(body.meta, left, inset + 66);
  hairline(ctx, left, inset + 96, textWidth, c.border);

  const top = inset + QUIZ_QUESTION_TOP;
  const footY = sheet.height - inset - QUIZ_FOOT_HEIGHT;

  if (indexes.length === 0) {
    // an empty set still opens: a dead white card reads as a broken render, this reads as a state
    ctx.font = font(400, 19, SANS);
    ctx.fillStyle = c.textMuted;
    ctx.fillText('Bộ câu hỏi này chưa có câu nào.', left, top + 8);
  } else if (questions[indexes[0]].solo) {
    const index = indexes[0];
    paintSoloQuestion(ctx, c, questions[index], quizQuestionNumber(questions[index], index), revealed, left, textWidth, top, footY);
  } else {
    const perBlock = (footY - top) / indexes.length;
    let y = top;
    for (let i = 0; i < indexes.length; i++) {
      const index = indexes[i];
      const question = questions[index];
      const fit = quizBlockFit(perBlock, question.options.length);
      y = paintQuizQuestion(ctx, c, question, quizQuestionNumber(question, index), revealed, left, textWidth, y, fit);
    }
  }

  hairline(ctx, left, footY, textWidth, c.border);
  ctx.font = font(600, 16, SANS);
  ctx.fillStyle = c.textMuted;
  ctx.textAlign = 'right';
  ctx.fillText(quizBodyFootLabel(questions, page), right, footY + 12);
  ctx.textAlign = 'left';

  return { canvas: sheet.canvas, aspect: QUIZ_ASPECT };
}
